import Link from 'next/link';
import { Info } from 'phosphor-icons';
import { c } from '../../../utils/tailwind-merge';
import { LinkV2 } from '../../atoms/Link/Link.v2';
import { Popover } from '../../atoms/Popover/Popover';
import { RichText } from '../../molecules/RichText/RichText';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../molecules/Table/Table';
import {
  ProductCompatibilityTableMultiPackagePropsDataWithMicrocopy,
  ProductCompatibilityTableProps,
  ProductCompatibilityTableSinglePackagePropsDataWithMicrocopy,
} from './ProductCompatibilityTable.types';

const ProductCompatibilitySinglePackage = ({
  unityVersion,
  renderPipelines,
  buildTarget,
  fileSize,
  latestVersion,
  latestReleaseDate,
  microcopy,
  className = '',
}: ProductCompatibilityTableSinglePackagePropsDataWithMicrocopy & {
  className?: string;
}) => {
  const formatValue = (value: string | string[]) =>
    Array.isArray(value) ? value.join(', ') : value;

  const rows = [
    { key: 'unityVersion', label: microcopy.unityVersion, value: unityVersion },
    {
      key: 'renderPipelines',
      label: microcopy.renderPipelines,
      value: renderPipelines,
    },
    { key: 'buildTarget', label: microcopy.buildTarget, value: buildTarget },
    { key: 'fileSize', label: microcopy.fileSize, value: fileSize },
    {
      key: 'latestVersion',
      label: microcopy.latestVersion,
      value: latestVersion,
    },
    {
      key: 'latestRelease',
      label: microcopy.latestRelease,
      value: latestReleaseDate,
    },
  ].filter((row) => row.label && row.value) as Array<{
    key: string;
    label: string;
    value: string | string[];
  }>;

  const LABEL_LENGTH_BREAK_THRESHOLD = 10;

  return (
    <Table className={className} role="presentation">
      <TableCaption>{microcopy?.title}</TableCaption>

      <TableHeader className="sr-only">
        <TableRow>
          <TableHead>{microcopy.headerColumn1}</TableHead>
          <TableHead>{microcopy.headerColumn2}</TableHead>
        </TableRow>
      </TableHeader>

      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.key}>
            <TableHead scope="row" className="!align-top">
              {row.key === 'renderPipelines' ? (
                <div className="inline-flex items-center">
                  <p className="tiny-text text-grey-700">{row.label}</p>
                  <Popover>
                    <Popover.Trigger
                      aria-label={microcopy.renderPipelinesTooltip}
                      className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-grey-500 hover:text-grey-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-1 focus-visible:ring-offset-white"
                    >
                      <Info size={16} weight="regular" aria-hidden="true" />
                    </Popover.Trigger>
                    <Popover.Content className="w-64 rounded-md border-transparent bg-grey-700 px-4 py-3 text-white shadow-card [&>div:nth-child(1)]:hidden [&>div:nth-child(2)]:hidden">
                      <div
                        aria-hidden="true"
                        className="absolute -top-1 left-1/2 h-3 w-3 -translate-x-1/2 rotate-45 bg-grey-700"
                      />
                      <p className="text-sm leading-7 text-white">
                        {microcopy.renderPipelinesTooltip}
                      </p>
                      <LinkV2 asChild size="small">
                        <Link
                          href={microcopy.renderPipelinesTooltipLinkHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`${microcopy.renderPipelinesTooltipLinkLabel} (${row.label})`}
                          className="text-sm leading-7 text-blue-300 no-underline hover:text-blue-200 hover:no-underline"
                        >
                          {microcopy.renderPipelinesTooltipLinkLabel}
                        </Link>
                      </LinkV2>
                    </Popover.Content>
                  </Popover>
                </div>
              ) : (
                row.label
              )}
            </TableHead>
            <TableCell
              className={c(
                'w-40',
                row.label.length > LABEL_LENGTH_BREAK_THRESHOLD
                  ? 'break-normal'
                  : 'break-all'
              )}
            >
              {formatValue(row.value)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const ProductCompatibilityMultiPackages = ({
  microcopy,
  className = '',
}: ProductCompatibilityTableMultiPackagePropsDataWithMicrocopy & {
  className?: string;
}) => {
  return (
    <div className={c('relative w-full max-w-[385px]', className)}>
      <p className="body-small-medium text-muted-foreground mb-4 break-all text-left">
        {microcopy.title}
      </p>
      <RichText
        className={c(
          'tiny-text',
          '[&_a]:tiny-text-link [&_a]:text-blue-hover [&_a]:no-underline ',
          '[&_a:visited]:tiny-text-link'
        )}
        html={microcopy.description}
      />
    </div>
  );
};

export const ProductCompatibilityTable = ({
  microcopy,
  ...props
}: ProductCompatibilityTableProps) => {
  if (props.variant === 'single-package') {
    return (
      <ProductCompatibilitySinglePackage {...props} microcopy={microcopy} />
    );
  }

  return <ProductCompatibilityMultiPackages {...props} microcopy={microcopy} />;
};
